import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKCheckMark,
    DKTooltipWrapper,
    DKIcon,
    DKIcons,
    DKSpinner,
    Toggle,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    showAlert,
    TOAST_TYPE
} from "deskera-ui-library";
import { TENANT, TenantService } from "../../../managers/TenantManagerService";
import TimesheetService from "../TimesheetService";
import DKInputDateTime from "../../common/DKInputDateTime";
import moment from 'moment';
import TimesheetUtils from "../TimesheetUtils";
import { ENTRY_STATUS } from "../TimesheetConstants";
import { convertDateToJson, reverseTimezoneOffset } from "../../../managers/common/CommonUtil";

function AddTimesheetEntry(props) {
    const { t } = useTranslation();

    const initialFormState = {
        task: {
            id: "task",
            title: t("Task"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]]
        },
        project: {
            id: "project",
            title: t("Project"),
            type: INPUT_TYPE.TEXT,
            readOnly: true
        },
        customer: {
            id: "customer",
            title: t("Customer"),
            type: INPUT_TYPE.TEXT,
            readOnly: true
        },
        entryDate: {
            id: "entryDate",
            title: t("Date"),
            type: INPUT_TYPE.DATE,
            validation: [["required"]],
            value: new Date()
        },
        fromDate: {
            id: "fromDate",
            title: t("From"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: new Date()
        },
        toDate: {
            id: "toDate",
            title: t("To"),
            type: "date-time-input",
            validation: [["required"]],
            is12Hour: true,
            hideFooterDateTime: false,
            value: new Date()
        },
        isBillable: {
            id: "isBillable",
            title: t("Billable"),
            type: "toggle",
            value: false
        }
    }

    const [formState, setFormState] = useState(initialFormState);
    const [fetchingDependencies, setFetchingDependencies] = useState(true);

    useEffect(() => {
        fetchDependencies();

        return removeElement;
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            const newState = { ...formState };

            if (Array.isArray(props.tasks)) {
                newState.task.options = props.tasks.map((task) => {
                    return {
                        value: task.id,
                        label: task.name
                    }
                });
            }


            if (props.isEdit) {
                setTimesheetEntryData(newState, props.entryData);
            }

            if (props.entryDate) {
                newState.entryDate.value = props.entryDate;
            }

            if (props.fromDate) {
                newState.fromDate.value = props.fromDate;
            }

            if (props.toDate) {
                newState.toDate.value = props.toDate;
            }

            setFormState(newState);
        }
    }, [fetchingDependencies]);

    const removeElement = () => {
        if (props.elementId) {
            const element = document.getElementById(props.elementId);
            if (element) {
                ReactDOM.unmountComponentAtNode(element);
                element.remove();
            }
        }
    }

    const fetchDependencies = async () => {
        setFetchingDependencies(false);
    }

    const setTimesheetEntryData = (newState, entryData) => {
        const task = TimesheetUtils.findTask(props.tasks, entryData.jobId);
        if (task) {
            newState.task.value = getDropdownLabel(entryData.jobId, newState.task.options);
            newState.project.value = entryData.projectName;
            newState.customer.value = entryData.customerName;
            newState.entryDate.value = new Date(moment(entryData.fromDate));
            newState.fromDate.value = moment(entryData.fromDate);
            newState.toDate.value = entryData.toDate ? moment(entryData.toDate):moment();
            newState.isBillable.value = entryData.isBillable;
        }
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.value === v);
        if (index !== -1) {
            value = o[index].label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }
        switch(field.id) {
            case "fromDate":
            case "toDate":
                if(field.invalid) {
                    error.invalid = true;
                    error.errorMessage = 'From time should not be less than To Time';
                } else {
                    error.invalid = false;
                    error.errorMessage = null;
                    break;
                }
                break;
            default :
                break;
        }

        return error;
    }

    const updateState = (stateName, field, value) => {
        const newState = { ...formState };
        newState[stateName] = { ...field, value: value, ...hasError(field, value) };
        updateValueAndValidity(stateName, newState);

        setFormState(newState);
    }

    const updateValueAndValidity = (stateName, state) => {
        switch (stateName) {
            case "task":
                const taskId = getDropdownValue(state.task.value, state.task.options);
                if (taskId) {
                    const task = TimesheetUtils.findTask(props.tasks, taskId);
                    if (task) {
                        state.project.value = task.projectName;
                        state.customer.value = task.customerName;
                        state.isBillable.value = task.isBillable;
                    }
                }
                break;
            case "fromDate":
            case "toDate":
                const from = state.fromDate.value;
                const to = state.toDate.value;
                if(moment(from).isAfter(to,'minute')) {
                    state[stateName] = {...state[stateName],...{
                        invalid: true,
                        errorMessage: 'From time should not be less than To Time'
                    }}
                } else {
                    state['fromDate'] = {...state['fromDate'],...{
                        invalid: false,
                        errorMessage: ''
                    }}
                    state['toDate'] = {...state['toDate'],...{
                        invalid: false,
                        errorMessage: ''
                    }}
                }
                break;
            default:
                break;
        }
    }

    const isInvalid = () => {
        let invalid = false;

        const newState = { ...formState };
        Object.entries(newState).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(newState);

        return invalid;
    }

    const getPayload = () => {
        const taskId = getDropdownValue(formState.task.value, formState.task.options);
        const task = TimesheetUtils.findTask(props.tasks, taskId);

        const sTime = new Date(formState.fromDate.value);
        const fromDate = new Date(formState.entryDate.value);
        fromDate.setHours(sTime.getHours());
        fromDate.setMinutes(sTime.getMinutes());


        const eTime = new Date(formState.toDate.value);
        const toDate = new Date(formState.entryDate.value);
        toDate.setHours(eTime.getHours());
        toDate.setMinutes(eTime.getMinutes());
        const payload = {
            employeeId: TenantService.get(TENANT.USER_ID),
            fromDate: moment(fromDate).format(),
            toDate: moment(toDate).format(),
            customer: (props.isEdit && props.entryData.customerName === formState.customer.value ) ? props.entryData.customer : task.customer,
            customerName: formState.customer.value,
            projectName: formState.project.value,
            projectId: (props.isEdit && props.entryData.projectName === formState.project.value ) ? props.entryData.projectId : task.projectId,
            jobId: taskId,
            description: formState.task.value,
            isBillable: formState.isBillable.value,
            isSynchronous: false,
            status: props.isEdit ? props.entryData.status : ENTRY_STATUS.DRAFT
        }

        if (props.isEdit) {
            payload.id = props.entryData.id;
        }

        return payload;
    }
    const onDraft=(data)=>{
        if(props.entryData.id) {
            if (data.status === "APPROVED" ) {
                
                return;
            }
            let payload = {
                ids: [`${props.entryData.id}`]
            };
            TimesheetService.draftTimesheetEntry(payload).then(() => {
                showToast("Timesheets updated successfully and save as a draft.", TOAST_TYPE.SUCCESS);
                removeLoader();
                removeElement();
                props.onSave();
            }, (error) => {
                showToast("Timesheets updated successfully.", TOAST_TYPE.SUCCESS);
                removeLoader();
                removeElement();
                props.onSave();
            })  
        } else {
            showToast("Timesheets updated successfully.", TOAST_TYPE.SUCCESS);
            removeLoader();
            removeElement();
            props.onSave();
        }
    }
    const saveTimesheetEntry = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();
        if (props.isEdit) {
            showLoader("Updating timesheet entry... Please wait");
            TimesheetService.updateTimesheetEntry(payload).then(
                (response) => {
                    if(props.entryData.status === ENTRY_STATUS.REJECTED) {
                        onDraft(payload)
                    } else {
                        showToast("Timesheets updated successfully.", TOAST_TYPE.SUCCESS);
                        removeLoader();
                        removeElement();
                        props.onSave();
                    }
                }, (error) => {
                    removeLoader();
                    removeElement();

                    if (error?.message) {
                        showAlert('Error',error.message);
                    } else {
                        showToast("Error while updating timesheet entry, Please try after sometime.", TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader("Adding timesheet entry... Please wait");
            TimesheetService.saveTimesheetEntry(payload).then(
                (response) => {
                    showToast("Timesheets added successfully.", TOAST_TYPE.SUCCESS);
                    removeLoader();
                    removeElement();
                    props.onSave();
                }, (error) => {
                    removeLoader();
                    removeElement();

                    if (error?.message) {
                        showAlert('Error',error.message);
                    } else {
                        showToast("Error while adding timesheet entry, Please try after sometime.", TOAST_TYPE.FAILURE);
                    }
                }
            );
        }
    }

    const renderField = (stateName, field, width, className) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
            case INPUT_TYPE.DATE:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                ref={field.ref}
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={field.validator}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    }
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => { },
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.CHECKBOX:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className="row position-relative pb-l mb-s">
                            <div className="d-flex dkinput-header-section mr-l">
                                <DKLabel className="mb-xs" text={`${field.title} ${isRequired(field.validation) ? "*" : ""}`} />
                                {field.tooltip &&
                                    <DKTooltipWrapper content={field.tooltip} tooltipClassName="checkbox-tooltip-prop">
                                        <DKIcon src={DKIcons.ic_info} className="ic-s ml-s cursor-hand" style={{ opacity: 0.4 }} />
                                    </DKTooltipWrapper>
                                }
                            </div>
                            <DKCheckMark
                                isSelected={field.value}
                                color="bg-app"
                                onClick={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "toggle":
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKLabel style={{ width: "46%" }} className="mb-xs" text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                            <Toggle
                                style={{ width: "20%" }}
                                isOn={field.value}
                                onChange={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "date-time-input":
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInputDateTime
                                className="parent-width"
                                is12Hour={field.is12Hour}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                title={isRequired(field.validation) ? `${field.title} *` : field.title}
                                hideLabel={!!field.hideLabel}
                                hideCalendar={true}
                                hideFooterDateTime={field.hideFooterDateTime}
                                selectedDate={moment(field.value, "hh:mm A").format()}
                                readOnly={!!field.readOnly}
                                errorMessage={field.errorMessage}
                                invalid={field.invalid}
                                onSave={(date) => {
                                    updateState(stateName, field, new Date(date));
                                }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden",maxWidth: 510 }}>
                <div className="parent-width pb-s" style={{ height: "70vh" }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={props.isEdit ? t("Edit Timesheet Entry") : t("Add Timesheet Entry")} className="fs-l fw-b" />
                        {fetchingDependencies && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                        <div className="ml-auto d-flex">
                            <DKButton
                                title={t("CANCEL")}
                                className="border-radius-m bg-gray1 border-m"
                                onClick={() => {
                                    removeElement();
                                }}
                            />
                            <DKButton
                                title={t("SAVE")}
                                className="bg-app text-white ml-m"
                                onClick={() => {
                                    saveTimesheetEntry();
                                }}
                            />
                        </div>
                    </div>
                    <div className="parent-width custom-scroll" style={{ overflow: "auto", height: "calc(100% - 69px)" }}>
                        <div className="p-xl">
                            {renderField("task", formState.task, "100%")}
                            {renderField("project", formState.project, "100%")}
                            {renderField("customer", formState.customer, "100%")}
                            {renderField("entryDate", formState.entryDate, "100%")}
                            {renderField("fromDate", formState.fromDate, "100%")}
                            {renderField("toDate", formState.toDate, "100%")}
                            {renderField("isBillable", formState.isBillable, "100%")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddTimesheetEntry;
